/**
 * Flashcard Card Component
 * Displays a single flashcard with flip animation
 */

import {useState} from 'react';
import {cn} from '../../utils/cn';

interface FlashcardCardProps {
  front: string;
  back: string;
  index: number;
  total: number;
  onNext: () => void;
  onPrevious: () => void;
  onMarkKnown?: () => void;
  onMarkUnknown?: () => void;
}

export function FlashcardCard({
  front,
  back,
  index,
  total,
  onNext,
  onPrevious,
  onMarkKnown,
  onMarkUnknown,
}: FlashcardCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Progress Indicator */}
      <div className="mb-4 text-center">
        <span className="text-sm text-textSecondary dark:text-[#858585]">
          Card {index + 1} of {total}
        </span>
        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="bg-orange-600 dark:bg-orange-500 h-2 rounded-full transition-all duration-300"
            style={{width: `${((index + 1) / total) * 100}%`}}
          ></div>
        </div>
      </div>

      {/* Flashcard */}
      <div
        className={cn(
          'relative w-full h-96 perspective-1000 cursor-pointer',
          'transform-gpu transition-transform duration-500'
        )}
        onClick={handleFlip}
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          className={cn(
            'absolute inset-0 rounded-lg border-2 shadow-lg transition-all duration-500',
            'backface-hidden',
            isFlipped ? 'rotate-y-180 opacity-0' : 'rotate-y-0 opacity-100',
            'bg-white dark:bg-[#252526] border-gray-300 dark:border-gray-600'
          )}
          style={{
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            backfaceVisibility: 'hidden',
          }}
        >
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center">
              <div className="text-sm text-textTertiary dark:text-[#6A6A6A] mb-4">Question</div>
              <p className="text-xl font-medium text-textPrimary dark:text-[#CCCCCC] leading-relaxed">
                {front}
              </p>
              <div className="mt-6 text-xs text-textTertiary dark:text-[#6A6A6A]">
                Click to flip
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'absolute inset-0 rounded-lg border-2 shadow-lg transition-all duration-500',
            'backface-hidden',
            isFlipped ? 'rotate-y-0 opacity-100' : 'rotate-y-180 opacity-0',
            'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
          )}
          style={{
            transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(180deg)',
            backfaceVisibility: 'hidden',
          }}
        >
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center">
              <div className="text-sm text-orange-700 dark:text-orange-300 mb-4">Answer</div>
              <p className="text-xl font-medium text-textPrimary dark:text-[#CCCCCC] leading-relaxed">
                {back}
              </p>
              <div className="mt-6 text-xs text-textTertiary dark:text-[#6A6A6A]">
                Click to flip back
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsFlipped(false);
            onPrevious();
          }}
          disabled={index === 0}
          className={cn(
            'px-4 py-2 rounded-lg font-medium transition-colors',
            index === 0
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'bg-backgroundSecondary dark:bg-[#252526] text-textPrimary dark:text-[#CCCCCC] hover:bg-gray-200 dark:hover:bg-[#3E3E42]'
          )}
        >
          ← Previous
        </button>

        <div className="flex items-center gap-2">
          {onMarkUnknown && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFlipped(false);
                onMarkUnknown();
              }}
              className="px-4 py-2 rounded-lg font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
            >
              Don't Know
            </button>
          )}
          {onMarkKnown && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFlipped(false);
                onMarkKnown();
              }}
              className="px-4 py-2 rounded-lg font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors"
            >
              Know It
            </button>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsFlipped(false);
            onNext();
          }}
          disabled={index === total - 1}
          className={cn(
            'px-4 py-2 rounded-lg font-medium transition-colors',
            index === total - 1
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'bg-backgroundSecondary dark:bg-[#252526] text-textPrimary dark:text-[#CCCCCC] hover:bg-gray-200 dark:hover:bg-[#3E3E42]'
          )}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

